import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../../../contexts/AuthContext';
import { apiRequest } from '../../../../../common/apiUtils';
import { useDataContext } from '../../../../../contexts/DataContext';

const fetchBatchedNodeData = async (token, sandboxId, nodeRequests, schemaObjects) => {
  // Group requests by node type
  const requestsByType = {
    item: [],
    vendor: [],
    loot: []
  };
  
  nodeRequests.forEach(request => {
    if (requestsByType[request.nodeType]) {
      requestsByType[request.nodeType].push(request.nodeId);
    }
  });
  
  const results = new Map();
  
  // Make batched requests for each type
  for (const [nodeType, nodeIds] of Object.entries(requestsByType)) {
    if (nodeIds.length === 0) continue;
    
    const typeMap = {
      'item': 'Items',
      'vendor': 'Vendors',
      'loot': 'Loot'
    };
    
    const schemaKey = typeMap[nodeType];
    const schema = schemaObjects[schemaKey];
    
    if (!schema) continue;
    
    const endpoint = schema.fetchAllEndpoint;
    if (!endpoint) continue;
    
          const replacements = { sandbox_id: sandboxId };
      
      // For batch requests, build query manually to create repeated parameters
      // This should create: item_ids=id1&item_ids=id2&item_ids=id3
      const paramName = `${nodeType}_ids`;
      
      // Build the query string manually
      const queryParts = nodeIds.map(nodeId => `${paramName}=${encodeURIComponent(nodeId)}`);
      const fullQueryString = queryParts.join('&');
      
      // For now, let's try using a different approach - modify the endpoint URL directly
      const originalUrl = endpoint.path || endpoint.url || endpoint;
      const separator = originalUrl.includes('?') ? '&' : '?';
      const modifiedEndpoint = {
        ...endpoint,
        path: `${originalUrl}${separator}${fullQueryString}`
      };
      
            const extraOptions = {};
      
      try {
        const response = await apiRequest(token, modifiedEndpoint, replacements, null, {}, extraOptions);
        const data = response.data || [];
      
      // Map results by ID for easy lookup
      data.forEach(item => {
        const idField = `${nodeType}_id`;
        if (item[idField]) {
          results.set(`${nodeType}_${item[idField]}`, item);
        }
      });
    } catch (error) {
      console.error(`Error fetching ${nodeType} batch data:`, error);
      // For batch requests, we'll just skip failed types and continue
    }
  }
  
  return results;
};

const fetchNodeData = async (token, sandboxId, nodeId, nodeType, schemaObjects) => {
  // For single node fetches, use the old individual approach to avoid the batching issue
  const typeMap = {
    'item': 'Items',
    'vendor': 'Vendors',
    'loot': 'Loot'
  };
  
  const schemaKey = typeMap[nodeType];
  const schema = schemaObjects[schemaKey];
  
  if (!schema) {
    throw new Error(`Schema not found for ${nodeType}`);
  }
  
  const endpoint = schema.fetchAllEndpoint;
  if (!endpoint) {
    throw new Error(`Endpoint not found for ${nodeType}`);
  }
  
  const replacements = { sandbox_id: sandboxId };
  const extraOptions = {
    queryParams: { [`${nodeType}_ids`]: nodeId }
  };
  
  try {
    const response = await apiRequest(token, endpoint, replacements, null, {}, extraOptions);
    const result = response.data?.[0] || null;
    
    if (!result) {
      throw new Error(`${nodeType.charAt(0).toUpperCase() + nodeType.slice(1)} with ID "${nodeId}" not found. Please check the ID and try again.`);
    }
    
    return result;
  } catch (error) {
    console.error(`Error fetching ${nodeType} data:`, error);
    if (error.message?.includes('not found') || error.status === 404) {
      throw error;
    }
    throw new Error(`Failed to fetch ${nodeType} data: ${error.message || 'Unknown error'}`);
  }
};

const fetchRelatedNodes = async (token, sandboxId, nodeType, filters, schemaObjects) => {
  const typeMap = {
    'item': 'Items',
    'vendor': 'Vendors',
    'loot': 'Loot'
  };
  
  const schemaKey = typeMap[nodeType];
  const schema = schemaObjects[schemaKey];
  
  if (!schema) return [];
  
  // Use the fetchAllEndpoint which is the collection endpoint
  const endpoint = schema.fetchAllEndpoint;
  if (!endpoint) return [];
  
  const replacements = { sandbox_id: sandboxId };
  const extraOptions = { queryParams: filters };
  
  try {
    const response = await apiRequest(token, endpoint, replacements, null, {}, extraOptions);
    return response.data || [];
  } catch (error) {
    console.error(`Error fetching ${nodeType} list:`, error);
    // For related nodes, just return empty array to prevent cascade failures
    return [];
  }
};

const buildTreeData = async (token, sandboxId, rootId, rootType, expandedNodes, schemaObjects, autoExpand = false) => {
  const nodes = [];
  const edges = [];
  const processedNodes = new Set();
  
  // Helper to create node ID
  const createNodeId = (type, id) => `${type}_${id}`;
  
  // Helper to add a node
  const addNode = (type, data, parentId = null, relationshipLabel = null) => {
    const nodeId = createNodeId(type, data[`${type}_id`]);
    
    if (processedNodes.has(nodeId)) {
      return nodeId;
    }
    
    processedNodes.add(nodeId);
    
    const node = {
      id: nodeId,
      type: type,
      data: {
        label: data.name || `${type} ${data[`${type}_id`]}`,
        [`${type}Id`]: data[`${type}_id`],
        hasChildren: false,
        isExpanded: expandedNodes.has(nodeId),
        rawData: data,
      },
      position: { x: 0, y: 0 }, // Will be calculated by dagre
    };
    
    nodes.push(node);
    
    if (parentId && relationshipLabel) {
      edges.push({
        id: `${parentId}-${nodeId}`,
        source: parentId,
        target: nodeId,
        type: 'smoothstep',
        style: { stroke: '#888', strokeWidth: 2 },
        label: relationshipLabel,
        labelStyle: { fill: '#ccc', fontSize: '11px', fontWeight: 'bold' },
        labelBgStyle: { fill: '#2d2d2d', fillOpacity: 0.8 },
        labelBgPadding: [4, 8],
        labelBgBorderRadius: 4,
      });
    }
    
    return nodeId;
  };
  
  // Process nodes recursively, but with smarter batching
  const processNode = async (nodeId, nodeType, parentId = null, relationshipLabel = null, depth = 0) => {
    if (depth > 5) return; // Prevent infinite recursion
    
    // Collect all IDs we need to fetch at this level
    const currentLevelNodes = [{ nodeId, nodeType }];
    const nodesToProcess = [];
    
    // Check if this node is expanded (or if we're auto-expanding)
    const shouldExpand = expandedNodes.has(createNodeId(nodeType, nodeId)) || autoExpand;
    
    // Fetch current level nodes in batch
    const batchResults = await fetchBatchedNodeData(token, sandboxId, currentLevelNodes, schemaObjects);
    
    // Process each node from the batch
    for (const [key, nodeData] of batchResults.entries()) {
      const [type, id] = key.split('_');
      
      if (!nodeData) continue;
      
      const newNodeId = addNode(type, nodeData, parentId, relationshipLabel);
      
      // Determine if this node has children
      let hasChildren = false;
      const childrenToProcess = [];
      
      if (shouldExpand) {
        if (type === 'item') {
          if (nodeData.entitled_loot_id) {
            hasChildren = true;
            childrenToProcess.push({
              nodeId: nodeData.entitled_loot_id, 
              nodeType: 'loot', 
              parentId: newNodeId, 
              label: 'Entitled Loot'
            });
          }
          if (nodeData.level_vendor_id) {
            hasChildren = true;
            childrenToProcess.push({
              nodeId: nodeData.level_vendor_id, 
              nodeType: 'vendor', 
              parentId: newNodeId, 
              label: 'Level Vendor'
            });
          }
        } else if (type === 'loot') {
          if (nodeData.item_id) {
            hasChildren = true;
            childrenToProcess.push({
              nodeId: nodeData.item_id, 
              nodeType: 'item', 
              parentId: newNodeId, 
              label: 'Item'
            });
          }
          if (nodeData.sub_vendor_id) {
            hasChildren = true;
            childrenToProcess.push({
              nodeId: nodeData.sub_vendor_id, 
              nodeType: 'vendor', 
              parentId: newNodeId, 
              label: 'Sub Vendor'
            });
          }
                           } else if (type === 'vendor') {
            // Use embedded loot data from vendor response if available, otherwise fetch separately
            let lootItems = [];
            
            // Check if vendor data includes loot array
            if (nodeData.loot && Array.isArray(nodeData.loot)) {
              lootItems = nodeData.loot;
            } else {
              // Fallback to separate API call if loot data not embedded
              try {
                lootItems = await fetchRelatedNodes(token, sandboxId, 'loot', { vendor_ids: id }, schemaObjects);
              } catch (error) {
                console.error(`Error fetching loot for vendor ${id}:`, error);
                lootItems = [];
              }
            }
            
            if (lootItems.length > 0) {
              hasChildren = true;
              
              // Sort loot items by sort_order (ascending), with null/undefined values at the end
              const sortedLootItems = lootItems.sort((a, b) => {
                const sortOrderA = a.sort_order;
                const sortOrderB = b.sort_order;
                
                // Handle null/undefined values
                if (sortOrderA == null && sortOrderB == null) return 0;
                if (sortOrderA == null) return 1;
                if (sortOrderB == null) return -1;
                
                return sortOrderA - sortOrderB;
              });
              
              // If we should expand, batch fetch all related items and vendors from the loot
              let batchedRelatedData = new Map();
              if (shouldExpand) {
                // Collect all unique item and vendor IDs from the loot
                const itemIds = new Set();
                const vendorIds = new Set();
                
                for (const loot of sortedLootItems.slice(0, 20)) {
                  if (loot.item_id && !processedNodes.has(`item_${loot.item_id}`)) {
                    itemIds.add(loot.item_id);
                  }
                  if (loot.sub_vendor_id && !processedNodes.has(`vendor_${loot.sub_vendor_id}`)) {
                    vendorIds.add(loot.sub_vendor_id);
                  }
                }
                
                // Batch fetch all needed items and vendors
                const batchRequests = [];
                if (itemIds.size > 0) {
                  batchRequests.push(...Array.from(itemIds).map(itemId => ({ nodeId: itemId, nodeType: 'item' })));
                }
                if (vendorIds.size > 0) {
                  batchRequests.push(...Array.from(vendorIds).map(vendorId => ({ nodeId: vendorId, nodeType: 'vendor' })));
                }
                
                if (batchRequests.length > 0) {
                  batchedRelatedData = await fetchBatchedNodeData(token, sandboxId, batchRequests, schemaObjects);
                }
              }
              
              // Process sorted loot items (limit to 20) using the data we already have
              for (const loot of sortedLootItems.slice(0, 20)) {
                if (loot.loot_id && !processedNodes.has(`loot_${loot.loot_id}`)) {
                  // Create the loot node directly using the data we already have
                  const lootNodeId = addNode('loot', loot, newNodeId, 'Vendor Loot');
                  
                  // Update hasChildren for the loot node
                  const lootNode = nodes.find(n => n.id === lootNodeId);
                  if (lootNode) {
                    lootNode.data.hasChildren = !!(loot.item_id || loot.sub_vendor_id);
                  }
                  
                  // If this loot should be expanded, process its children using batched data
                  if (shouldExpand) {
                    // Process item child using batched data
                    if (loot.item_id) {
                      const itemData = batchedRelatedData.get(`item_${loot.item_id}`);
                      if (itemData && !processedNodes.has(`item_${loot.item_id}`)) {
                        const itemNodeId = addNode('item', itemData, lootNodeId, 'Item');
                        const itemNode = nodes.find(n => n.id === itemNodeId);
                        if (itemNode) {
                          itemNode.data.hasChildren = !!(itemData.entitled_loot_id || itemData.level_vendor_id);
                        }
                        
                        // Recursively process item's children if needed
                        const itemChildrenToProcess = [];
                        if (itemData.entitled_loot_id) {
                          itemChildrenToProcess.push({
                            nodeId: itemData.entitled_loot_id,
                            nodeType: 'loot',
                            parentId: itemNodeId,
                            label: 'Entitled Loot'
                          });
                        }
                        if (itemData.level_vendor_id) {
                          itemChildrenToProcess.push({
                            nodeId: itemData.level_vendor_id,
                            nodeType: 'vendor',
                            parentId: itemNodeId,
                            label: 'Level Vendor'
                          });
                        }
                        
                        for (const child of itemChildrenToProcess) {
                          await processNode(child.nodeId, child.nodeType, child.parentId, child.label, depth + 3);
                        }
                      }
                    }
                    
                    // Process sub-vendor child using batched data
                    if (loot.sub_vendor_id) {
                      const subVendorData = batchedRelatedData.get(`vendor_${loot.sub_vendor_id}`);
                      if (subVendorData && !processedNodes.has(`vendor_${loot.sub_vendor_id}`)) {
                        const subVendorNodeId = addNode('vendor', subVendorData, lootNodeId, 'Sub Vendor');
                        const subVendorNode = nodes.find(n => n.id === subVendorNodeId);
                        if (subVendorNode) {
                          // Check if sub-vendor has loot (using embedded data or API check)
                          let hasSubVendorLoot = false;
                          if (subVendorData.loot && Array.isArray(subVendorData.loot)) {
                            hasSubVendorLoot = subVendorData.loot.length > 0;
                          } else {
                            try {
                              const subVendorLoot = await fetchRelatedNodes(token, sandboxId, 'loot', { vendor_ids: loot.sub_vendor_id, limit: 1 }, schemaObjects);
                              hasSubVendorLoot = subVendorLoot.length > 0;
                            } catch (error) {
                              console.error(`Error checking sub-vendor loot:`, error);
                            }
                          }
                          subVendorNode.data.hasChildren = hasSubVendorLoot;
                        }
                        
                        // Recursively process sub-vendor's children (its loot)
                        await processNode(loot.sub_vendor_id, 'vendor', lootNodeId, 'Sub Vendor', depth + 3);
                      }
                    }
                  }
                }
              }
            }
         }
      } else {
        // Just check if it has children without fetching them
        if (type === 'item') {
          hasChildren = !!(nodeData.entitled_loot_id || nodeData.level_vendor_id);
        } else if (type === 'loot') {
          hasChildren = !!(nodeData.item_id || nodeData.sub_vendor_id);
                 } else if (type === 'vendor') {
           // Check if vendor has loot using embedded data first
           if (nodeData.loot && Array.isArray(nodeData.loot)) {
             hasChildren = nodeData.loot.length > 0;
           } else {
             // Fallback to API call if loot data not embedded
             try {
               const lootItems = await fetchRelatedNodes(token, sandboxId, 'loot', { vendor_ids: id, limit: 1 }, schemaObjects);
               hasChildren = lootItems.length > 0;
             } catch (error) {
               console.error(`Error checking vendor loot:`, error);
             }
           }
         }
      }
      
      // Update hasChildren flag
      const node = nodes.find(n => n.id === newNodeId);
      if (node) {
        node.data.hasChildren = hasChildren;
      }
      
      // Add children to process queue
      nodesToProcess.push(...childrenToProcess);
    }
    
    // Process children recursively
    for (const child of nodesToProcess) {
      await processNode(child.nodeId, child.nodeType, child.parentId, child.label, depth + 1);
    }
  };
  
  // Start with root node
  await processNode(rootId, rootType, null, null, 0);
  
  return { nodes, edges };
};

export default function useTreeData(sandboxId, rootId, rootType, expandedNodes, autoExpand = false) {
  const { getAccessTokenSilently } = useAuth();
  const { dataContext } = useDataContext();
  const schemaObjects = dataContext.schemaObjects;
  
  return useQuery({
    queryKey: ['treeData', sandboxId, rootId, rootType, Array.from(expandedNodes).sort(), autoExpand],
    queryFn: async () => {
      if (!sandboxId || !rootId || !rootType) {
        return null;
      }
      
      if (!schemaObjects || Object.keys(schemaObjects).length === 0) {
        throw new Error('Schema data is not loaded yet. Please wait a moment and try again.');
      }
      
      const token = await getAccessTokenSilently();
      return buildTreeData(token, sandboxId, rootId, rootType, expandedNodes, schemaObjects, autoExpand);
    },
    enabled: !!(sandboxId && rootId && rootType && schemaObjects && Object.keys(schemaObjects).length > 0),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      // Don't retry if it's a "not found" error
      if (error.message?.includes('not found')) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });
} 